import React, { useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtom, useSetAtom } from 'jotai';
import useNetwork from '@smart-tv/hooks/use-network/use-network';
import { ArrowPressHandler } from '@noriginmedia/norigin-spatial-navigation';
import LogoNorlys from '@smart-tv/components/logo-norlys/logo-norlys';
import testIds from '@smart-tv/e2e/shared/variables';
import { ButtonProps } from '../button/button';
import { isTvGuideDateReset } from './header-helpers';
import HeaderNavigation, { links, controls } from './header-navigation';
import styles from './header.module.scss';
import { headerActiveAtom, topLevelPageReadyAtom } from '../../atoms';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import TvGuideContext from '@smart-tv/pages/tv-guide/utils/tv-guide-context';

interface Props {
  onLiveClick?: () => void;
  onArrowPress?: ArrowPressHandler<ButtonProps>;
}

export const Header: React.FC<Props> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useAtom(headerActiveAtom);
  const setTopLevelPageReady = useSetAtom(topLevelPageReadyAtom);
  const { checkAndUpdateNetwork, setIsOffline, setIsNetworkDialogSuppressed } = useNetwork();
  const { selectedDate } = React.useContext(TvGuideContext);

  useEffect(() => {
    const activeIndex = [...links, ...controls].findIndex((item) => {
      if (item.href === '/') return location.pathname === '/';
      return location.pathname === item.href;
    });

    if (activeIndex !== -1) setActive(activeIndex);
  }, [location.pathname, setActive]);

  const handleLinkPress = React.useCallback(
    async (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      href: string,
      index: number,
      focusKey: string
    ) => {
      event.preventDefault();

      setIsNetworkDialogSuppressed(false);
      setActive(index);

      const navigateToDestination = () => {
        if (!IS_BROWSER && window.location.pathname !== href) setTopLevelPageReady(false);
        navigate(href, IS_BROWSER ? undefined : { state: { focusFirst: true } });
      };

      const isResettingTvGuideDate = isTvGuideDateReset(focusKey, href, selectedDate.isToday);

      if (window.location.pathname === href && !isResettingTvGuideDate) {
        navigateToDestination();
        return;
      }

      setIsOffline(false);
      const hasNetwork = await checkAndUpdateNetwork();
      if (hasNetwork) {
        navigateToDestination();
      }
    },
    [
      navigate,
      setActive,
      setTopLevelPageReady,
      checkAndUpdateNetwork,
      setIsOffline,
      setIsNetworkDialogSuppressed,
      selectedDate.isToday
    ]
  );

  const handleLogoClick = useCallback(() => {
    window.location.reload();
    window.location.href = '/';
  }, []);

  return (
    <header className={styles['header']}>
      <div className={styles['header__container']}>
        <div className={styles['header__25']}>
          <LogoNorlys
            width={142}
            height={27}
            color={'rgba(242, 242, 242, 0.5)'}
            data-testid={testIds.header.logo}
            onClick={handleLogoClick}
          />
        </div>
        <HeaderNavigation onClick={handleLinkPress} active={active} />
      </div>
    </header>
  );
};

export default React.memo(Header);
